import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../../config';


interface Project {
  id: number;
  name: string;
  github_repo?: string;
  jira_project_key?: string;
}

interface ProjectIntegrationsProps {
  project: Project;
}

interface IntegrationData {
  github_token?: string;
  github_connected: boolean;
  jira_connected: boolean;
  jira_user_email?: string;
  jira_cloud_id?: string;
}

const ProjectIntegrations: React.FC<ProjectIntegrationsProps> = ({ project }) => {
  const [githubToken, setGithubToken] = useState('');
  const [githubRepos, setGithubRepos] = useState<string[]>([]);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [selectedRepo, setSelectedRepo] = useState(project.github_repo || '');

  const [jiraOAuthClientId, setJiraOAuthClientId] = useState('');
  const [jiraOAuthClientSecret, setJiraOAuthClientSecret] = useState('');

  const [integrationData, setIntegrationData] = useState<IntegrationData | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Repository status state
  const [repoStatus, setRepoStatus] = useState<{
    configured: boolean;
    cloned: boolean;
    path: string | null;
    message: string;
  } | null>(null);
  const [cloningRepo, setCloningRepo] = useState(false);

  useEffect(() => {
    loadIntegrations();
  }, [project.id]);

  useEffect(() => {
    if (integrationData?.github_connected) {
      loadGithubRepos();
      loadRepositoryStatus();
    }
  }, [integrationData?.github_connected]);

  useEffect(() => {
    if (selectedRepo) {
      loadRepositoryStatus();
    }
  }, [selectedRepo]);

  const loadIntegrations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/projects/${project.id}/integrations`);
      setIntegrationData(response.data);

      // Load OAuth credentials into form fields
      if (response.data.jira_oauth_client_id) {
        setJiraOAuthClientId(response.data.jira_oauth_client_id);
      }
      if (response.data.jira_oauth_client_secret) {
        setJiraOAuthClientSecret(response.data.jira_oauth_client_secret);
      }

      // Load selected repository from project data
      const projectResponse = await axios.get(`${API_URL}/api/projects/${project.id}`);
      if (projectResponse.data.github_repo) {
        setSelectedRepo(projectResponse.data.github_repo);
      }
    } catch (error) {
      console.error('Failed to load integrations:', error);
    }
  };

  const loadGithubRepos = async () => {
    setLoadingRepos(true);
    try {
      const response = await axios.get(`${API_URL}/api/projects/${project.id}/integrations/github/repos`);
      setGithubRepos(response.data.repos || []);
    } catch (error) {
      console.error('Failed to load repositories:', error);
    } finally {
      setLoadingRepos(false);
    }
  };

  const loadRepositoryStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/projects/${project.id}/repository/status`);
      setRepoStatus(response.data);
    } catch (error) {
      console.error('Failed to load repository status:', error);
    }
  };

  const handleCloneRepository = async () => {
    if (!window.confirm(`Clone repository ${selectedRepo}?\n\nThis will clone the repository to the server workspace.`)) {
      return;
    }

    setCloningRepo(true);
    try {
      const response = await axios.post(`${API_URL}/api/projects/${project.id}/repository/clone`);
      setMessage({ type: 'success', text: response.data.message });
      await loadRepositoryStatus(); // Reload status
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Failed to clone repository'
      });
    } finally {
      setCloningRepo(false);
    }
  };

  const handleSaveGithubToken = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!githubToken.trim()) {
      setMessage({ type: 'error', text: 'Please enter a valid GitHub token' });
      return;
    }

    setLoading(true);
    try {
      await axios.put(`${API_URL}/api/projects/${project.id}/integrations`, {
        github_token: githubToken
      });
      setMessage({ type: 'success', text: 'GitHub token saved successfully!' });
      setGithubToken('');
      await loadIntegrations();
      await loadGithubRepos();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to save GitHub token' });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectRepo = async (repo: string) => {
    try {
      await axios.put(`${API_URL}/api/projects/${project.id}`, {
        github_repo: repo
      });
      setSelectedRepo(repo);
      setMessage({ type: 'success', text: `Repository ${repo} selected` });
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to select repository' });
    }
  };

  const handleDisconnectGithub = async () => {
    if (!window.confirm('Are you sure you want to disconnect GitHub from this project?')) return;

    try {
      await axios.delete(`${API_URL}/api/projects/${project.id}/integrations/github`);
      setMessage({ type: 'success', text: 'GitHub disconnected successfully' });
      await loadIntegrations();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to disconnect GitHub' });
    }
  };

  const handleSaveJiraOAuth = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await axios.put(`${API_URL}/api/projects/${project.id}/integrations`, {
        jira_oauth_client_id: jiraOAuthClientId,
        jira_oauth_client_secret: jiraOAuthClientSecret
      });
      setMessage({ type: 'success', text: 'Jira OAuth credentials saved. Now click "Connect Jira" to authorize.' });

      // Reload integrations to verify credentials were saved
      await loadIntegrations();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to save Jira OAuth credentials' });
    }
  };

  const handleConnectJira = async () => {
    try {
      // Get OAuth URL from backend (authenticated request)
      const response = await axios.get(`${API_URL}/api/projects/${project.id}/integrations/jira/oauth`);
      const authUrl = response.data.auth_url;

      // Open the auth URL in a popup
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        authUrl,
        'JiraOAuth',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      const checkPopup = setInterval(() => {
        if (popup && popup.closed) {
          clearInterval(checkPopup);
          loadIntegrations();
        }
      }, 500);
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to initiate Jira OAuth' });
    }
  };

  const handleDisconnectJira = async () => {
    if (!window.confirm('Are you sure you want to disconnect Jira from this project?')) return;

    try {
      await axios.delete(`${API_URL}/api/projects/${project.id}/integrations/jira`);
      setMessage({ type: 'success', text: 'Jira disconnected successfully' });
      await loadIntegrations();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to disconnect Jira' });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Integrations: {project.name}
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Configure GitHub and Jira integrations for this project. Your tokens are stored securely and used only for this project.
        </p>
      </div>

      {/* Message Toast */}
      {message && (
        <div className={`p-4 rounded-lg border ${
          message.type === 'success'
            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-800 dark:text-green-300'
            : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-300'
        }`}>
          {message.text}
        </div>
      )}

      {/* GitHub Integration */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700 space-y-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3 flex-1">
            <div className="text-3xl">🐙</div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                GitHub
              </h3>
              {integrationData?.github_connected ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded text-sm font-semibold">
                      ✓ Connected
                    </span>
                  </div>

                  {/* Repository Selector */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      Select Repository
                    </label>
                    {loadingRepos ? (
                      <p className="text-sm text-gray-500">Loading repositories...</p>
                    ) : githubRepos.length > 0 ? (
                      <select
                        value={selectedRepo}
                        onChange={(e) => handleSelectRepo(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
                      >
                        <option value="">-- Select a repository --</option>
                        {githubRepos.map(repo => (
                          <option key={repo} value={repo}>{repo}</option>
                        ))}
                      </select>
                    ) : (
                      <p className="text-sm text-gray-500">No repositories found</p>
                    )}
                  </div>

                  {/* Repository Status & Clone */}
                  {selectedRepo && repoStatus && (
                    <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded border border-gray-200 dark:border-gray-700">
                      <div className="flex items-start gap-2">
                        <div className="text-lg mt-0.5">
                          {repoStatus.cloned ? '✅' : '📁'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                              Repository Status
                            </p>
                            <span className={`px-2 py-0.5 text-xs font-semibold rounded ${
                              repoStatus.cloned
                                ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300'
                                : 'bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300'
                            }`}>
                              {repoStatus.cloned ? 'Cloned' : 'Not Cloned'}
                            </span>
                          </div>

                          <code className="text-sm text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-800 px-2 py-1 rounded border border-gray-300 dark:border-gray-600 block break-all mb-2">
                            /workspace/{selectedRepo}
                          </code>

                          {!repoStatus.cloned && (
                            <>
                              <p className="text-xs text-gray-500 dark:text-gray-500 mb-2">
                                Repository needs to be cloned before you can run jobs
                              </p>
                              <button
                                onClick={handleCloneRepository}
                                disabled={cloningRepo}
                                className="w-full px-3 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded font-semibold transition"
                              >
                                {cloningRepo ? '🔄 Cloning...' : '📥 Clone Repository'}
                              </button>
                            </>
                          )}

                          {repoStatus.cloned && (
                            <p className="text-xs text-green-600 dark:text-green-400">
                              ✓ Repository is ready for jobs
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  <button
                    onClick={handleDisconnectGithub}
                    className="px-3 py-1.5 text-sm bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 rounded hover:bg-red-200 dark:hover:bg-red-800 transition"
                  >
                    Disconnect GitHub
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSaveGithubToken} className="space-y-3">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Connect GitHub to enable code operations for this project.
                  </p>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                      Personal Access Token
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="password"
                        value={githubToken}
                        onChange={(e) => setGithubToken(e.target.value)}
                        placeholder="ghp_xxxxxxxxxxxxxxxxxxxx"
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
                        required
                      />
                      <button
                        type="submit"
                        disabled={loading}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition disabled:opacity-50"
                      >
                        {loading ? 'Saving...' : 'Connect'}
                      </button>
                    </div>
                  </div>

                  <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900 p-3 rounded">
                    <p className="font-semibold mb-1">Required scopes:</p>
                    <ul className="list-disc list-inside ml-2 space-y-0.5">
                      <li><code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">repo</code> - Full control of repositories</li>
                      <li><code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">workflow</code> - Update GitHub Actions</li>
                    </ul>
                    <p className="mt-2">
                      <a
                        href="https://github.com/settings/tokens/new"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        Create a token →
                      </a>
                    </p>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Jira Integration */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700 space-y-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3 flex-1">
            <div className="text-3xl">🔷</div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                Jira
              </h3>
              {integrationData?.jira_connected ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded text-sm font-semibold">
                      ✓ Connected
                    </span>
                  </div>

                  {integrationData.jira_user_email && (
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        <strong>User:</strong> {integrationData.jira_user_email}
                      </p>
                      {integrationData.jira_cloud_id && (
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          <strong>Cloud ID:</strong> <code className="font-mono text-xs">{integrationData.jira_cloud_id}</code>
                        </p>
                      )}
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={handleConnectJira}
                      className="px-3 py-1.5 text-sm bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-800 transition"
                    >
                      Reconnect Jira (nové scopes)
                    </button>
                    <button
                      onClick={handleDisconnectJira}
                      className="px-3 py-1.5 text-sm bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 rounded hover:bg-red-200 dark:hover:bg-red-800 transition"
                    >
                      Disconnect Jira
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Pro změnu sprintu potřebuješ v Atlassian app i{' '}
                    <code className="text-[10px]">write:sprint:jira-software</code> a{' '}
                    <code className="text-[10px]">write:board-scope:jira-software</code>,
                    pak Reconnect.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Connect Jira to access issues and create tickets for this project.
                  </p>

                  {/* Step 1: OAuth Credentials */}
                  <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                      Step 1: OAuth Credentials
                    </h4>
                    <form onSubmit={handleSaveJiraOAuth} className="space-y-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                          OAuth Client ID
                        </label>
                        <input
                          type="text"
                          value={jiraOAuthClientId}
                          onChange={(e) => setJiraOAuthClientId(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono"
                          placeholder="Your Jira OAuth Client ID"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                          OAuth Client Secret
                        </label>
                        <input
                          type="password"
                          value={jiraOAuthClientSecret}
                          onChange={(e) => setJiraOAuthClientSecret(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono"
                          placeholder="Your Jira OAuth Client Secret"
                          required
                        />
                      </div>

                      <button
                        type="submit"
                        className="px-4 py-2 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition"
                      >
                        Save Credentials
                      </button>
                    </form>

                    <div className="mt-3 text-xs text-gray-600 dark:text-gray-400 space-y-1">
                      <p>Don't have OAuth credentials?</p>
                      <a
                        href="https://developer.atlassian.com/console/myapps/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        Create an OAuth 2.0 app →
                      </a>
                      <p className="pt-1">
                        Permissions: <code className="text-[10px]">read:jira-user</code>,{' '}
                        <code className="text-[10px]">read:jira-work</code>,{' '}
                        <code className="text-[10px]">write:jira-work</code>,{' '}
                        <code className="text-[10px]">read/write:sprint:jira-software</code>,{' '}
                        <code className="text-[10px]">read/write:board-scope:jira-software</code>
                      </p>
                    </div>
                  </div>

                  {/* Step 2: Connect */}
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                      Step 2: Authorize
                    </h4>
                    <button
                      onClick={handleConnectJira}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
                    >
                      Connect Jira
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectIntegrations;
